/**
 * Atualiza preços dos cards Andrade ao trocar variante (swatch) — layout categoria ML.
 */
import { ThemeEvents } from '@theme/events';
import { formatMoney } from '@theme/money-formatting';

/**
 * @param {HTMLElement} card
 */
function getPricingRoot(card) {
  if (card instanceof HTMLElement && card.hasAttribute('data-andrade-pricing')) return card;
  return card.querySelector('[data-andrade-pricing]');
}

/**
 * @param {number} priceCents
 * @param {number} installments
 */
function installmentCents(priceCents, installments) {
  const count = Math.max(1, installments);
  return Math.round((priceCents + Math.floor(count / 2)) / count);
}

/**
 * @param {HTMLElement} card
 * @param {{ price: number, compare_at_price?: number | null }} variant
 */
function updateCardPricing(card, variant) {
  const meta = getPricingRoot(card);
  if (!meta || !variant || variant.price == null) return;
  const visible = meta;

  const price = Number(variant.price);
  const autoPct = Number(meta.dataset.autoDiscountPercent || 0);
  const salePrice = autoPct > 0 ? Math.floor((price * (100 - autoPct)) / 100) : price;
  const compare = autoPct > 0 ? price : null;
  const pixRaw = meta.dataset.pixPercent;
  const pixPctNum = autoPct > 0 ? 0 : pixRaw == null || pixRaw === '' ? 0 : Number(pixRaw);
  const pixPct = pixPctNum / 100;
  const showPix = pixPctNum > 0;
  const instCount = Number(meta.dataset.installmentCount || 10) || 10;
  const moneyFormat = meta.dataset.moneyFormat || '{{amount}}';

  const pix = showPix ? Math.round(salePrice * (1 - pixPct)) : salePrice;
  const displayCents = showPix ? pix : salePrice;
  const inst = installmentCents(salePrice, instCount);

  let discountPct = 0;
  let showPixOffLabel = false;
  if (autoPct > 0) {
    discountPct = autoPct;
  } else if (showPix) {
    discountPct = pixPctNum;
    showPixOffLabel = true;
  }

  const compareEl = visible.querySelector('[data-md-card-compare]');
  const discountLabel = visible.querySelector('[data-md-card-discount-pill]');
  const instEl = visible.querySelector('[data-md-card-installments]');
  const wholeEl = visible.querySelector('[data-md-card-price-whole]');
  const centsEl = visible.querySelector('[data-md-card-price-cents]');
  const fullEl = visible.querySelector('[data-md-card-price-display]');
  const unavailable = visible.querySelector('.ml-card-info__unavailable');

  if (price <= 0) {
    if (unavailable) unavailable.classList.remove('hidden');
    visible.querySelector('.ml-card-info__price-row')?.classList.add('hidden');
    instEl?.classList.add('hidden');
    visible.querySelector('[data-md-card-shipping]')?.classList.add('hidden');
    return;
  }

  if (unavailable) unavailable.classList.add('hidden');
  visible.querySelector('.ml-card-info__price-row')?.classList.remove('hidden');

  const whole = Math.floor(displayCents / 100);
  const cents = displayCents % 100;
  const centsStr = cents < 10 ? `0${cents}` : String(cents);

  if (fullEl) {
    fullEl.textContent = formatMoney(displayCents, moneyFormat);
  } else if (wholeEl && centsEl) {
    wholeEl.textContent = String(whole);
    centsEl.textContent = centsStr;
  }

  if (compareEl) {
    if (compare) {
      compareEl.textContent = formatMoney(compare, moneyFormat);
      compareEl.classList.remove('hidden');
      compareEl.removeAttribute('hidden');
    } else {
      compareEl.classList.add('hidden');
      compareEl.setAttribute('hidden', '');
    }
  }

  const tplPixOff = meta.dataset.templatePixOff || '__PCT__% OFF no Pix';
  if (discountLabel) {
    if (discountPct > 0) {
      discountLabel.textContent = showPixOffLabel
        ? tplPixOff.replace('__PCT__', String(discountPct))
        : `${discountPct}% OFF`;
      discountLabel.classList.remove('hidden');
      discountLabel.dataset.pixOffLabel = showPixOffLabel ? 'true' : 'false';
    } else {
      discountLabel.classList.add('hidden');
    }
  }

  const tplInst = meta.dataset.templateInstallments || '';
  if (instEl && tplInst) {
    if (inst > 0) {
      instEl.textContent = tplInst
        .replace('__COUNT__', String(instCount))
        .replace('__PRICE__', formatMoney(inst, moneyFormat));
      instEl.classList.remove('hidden');
    } else {
      instEl.classList.add('hidden');
    }
  }

  const shipEl = visible.querySelector('[data-md-card-shipping]');
  const freeMin = Number(shipEl?.dataset.freeShippingMin || 0);
  if (shipEl) {
    const qualifies = freeMin > 0 && price >= freeMin;
    shipEl.textContent = qualifies ? shipEl.dataset.labelFree || '' : '';
    shipEl.classList.toggle('hidden', !qualifies);
  }

  meta.dataset.variantId = String(variant.id || meta.dataset.variantId || '');
  if (variant.syncList !== false) {
    meta.dataset.variantListPrice = String(price);
    meta.dataset.variantListCompare = '0';
  }
}

function refreshAllCardPricing() {
  document.querySelectorAll('[data-andrade-pricing]').forEach((meta) => {
    if (!(meta instanceof HTMLElement)) return;
    const card = meta.closest('product-card.andrade-ml-card, product-card.andrade-product-card, .ml-card-info');
    const list = Number(meta.dataset.variantListPrice || meta.dataset.variantPrice || 0);
    const listCompare = Number(meta.dataset.variantListCompare || 0);
    updateCardPricing(card || meta, {
      price: list,
      compare_at_price: listCompare > 0 ? listCompare : null,
      id: meta.dataset.variantId,
      syncList: false,
    });
  });
}

document.addEventListener(ThemeEvents.variantUpdate, (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;

  const card = target.closest('product-card.andrade-ml-card, product-card.andrade-product-card');
  if (!card || !event.detail?.resource) return;

  updateCardPricing(card, event.detail.resource);
});

document.addEventListener('andrade:vitrine', refreshAllCardPricing);
refreshAllCardPricing();
window.AndradeCardPricing = { refreshAll: refreshAllCardPricing };
